import { GroupedSection } from '@/components/ui/grouped-section';
import { Link } from 'react-router-dom';
import { buttonVariants } from '@/components/ui/button';
import { ArrowUpRightFromSquare } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { ExternalConnectionDto } from '@/lib/integrationTypes/externalConnectionTypes';
import { formatDistanceToNow } from 'date-fns';
import ExternalConnectionFiltersAlt from '@/features/ExternalConnections/Filters/ExternalConnectionFilters';
import { FieldValues } from 'react-hook-form';

interface DeviceConnectionChoicesProps {
  deviceCreationChoices: Record<string, string | null>;
  externalConnection?: ExternalConnectionDto;
  isLoading?: boolean;
  onChoicesChange?: (choices: Record<string, string | null>) => void;
}

export function DeviceConnectionChoices({
  deviceCreationChoices,
  externalConnection,
  isLoading,
  onChoicesChange,
}: DeviceConnectionChoicesProps) {
  const externalConnectionId = externalConnection?.id;
  const externalConnectionName = externalConnection?.name || 'VMS';
  const integrationBrand = externalConnection?.integration.name || "";

  const hasEditableFilters = externalConnection &&
    externalConnection.deviceCreationKeyValues?.length > 0 &&
    externalConnection.deviceCreationChoices?.length > 0;

  if (isLoading) {
    return (
      <GroupedSection
        title="VMS Connection"
        description="Device selection from VMS"
      >
        <div className="space-y-3">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-4 w-56" />
        </div>
      </GroupedSection>
    );
  }

  return (
    <GroupedSection
      title={`${integrationBrand} Connection`}
      description={`This device is managed centrally through the ${externalConnectionName}.`}
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border border-[#2E3835] bg-card1 p-4">
          <div>
            <span className="text-sm text-gray-400">Connected to</span>
            <p className="text-base font-medium text-white">{externalConnectionName}</p>
            {externalConnection?.updated && (
              <span className="text-xs text-gray-500">
                Last scanned {formatDistanceToNow(new Date(externalConnection.updated), { addSuffix: true })}
              </span>
            )}
          </div>
          {externalConnectionId && (
            <Link
              to={`/connections/${externalConnectionId}`}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonVariants({ variant: 'outline', size: 'sm' })}
            >
              <ArrowUpRightFromSquare className="mr-2 h-4 w-4" />
              View Connection
            </Link>
          )}
        </div>

        {hasEditableFilters ? (
          <div className="rounded-lg border border-[#2E3835] bg-card1 p-4">
            <h4 className="mb-1 text-sm font-medium text-gray-400">Device Selection</h4>
            <ExternalConnectionFiltersAlt
              externalConnection={externalConnection}
              values={deviceCreationChoices as FieldValues}
              onChange={(data) => onChoicesChange?.(data as Record<string, string | null>)}
              processing={false}
            />
          </div>
        ) : (
          deviceCreationChoices && Object.keys(deviceCreationChoices).length > 0 && (
            <div className="rounded-lg border border-[#2E3835] bg-card1 p-4">
              <h4 className="mb-3 text-sm font-medium text-gray-400">Device Selection</h4>
              <div className="space-y-2">
                {Object.entries(deviceCreationChoices).map(([key, value]) => {
                  if (value === null || value === undefined) return null;
                  return (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">{key}</span>
                      <span className="text-sm font-medium text-white">{value}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )
        )}
      </div>
    </GroupedSection>
  );
}
